
import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { X } from 'lucide-react';
import ImageUpload from '@/components/shared/ImageUpload';

interface ClientLogo {
  id: string;
  name: string;
  logo_url: string;
  website_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface ClientLogoFormProps {
  logo?: ClientLogo | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ClientLogoForm = ({ logo, onClose, onSuccess }: ClientLogoFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    display_order: 0,
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    if (logo) {
      setFormData({
        name: logo.name,
        logo_url: logo.logo_url,
        website_url: logo.website_url || '',
        display_order: logo.display_order,
        is_active: logo.is_active,
      });
    }
  }, [logo]);

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (logo) {
        const { error } = await supabase
          .from('client_logos')
          .update({
            name: data.name,
            logo_url: data.logo_url,
            website_url: data.website_url || null,
            display_order: data.display_order,
            is_active: data.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', logo.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('client_logos')
          .insert({
            name: data.name,
            logo_url: data.logo_url,
            website_url: data.website_url || null,
            display_order: data.display_order,
            is_active: data.is_active,
          });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: `Client logo ${logo ? 'updated' : 'created'} successfully`,
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to ${logo ? 'update' : 'create'} client logo`,
        variant: "destructive",
      });
      console.error('Form error:', error);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.logo_url.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    mutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{logo ? 'Edit Client Logo' : 'Add Client Logo'}</CardTitle>
              <CardDescription>
                {logo ? 'Update client logo information' : 'Add a new client logo to the carousel'}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter client name"
                required
              />
            </div>

            <ImageUpload
              currentImageUrl={formData.logo_url}
              onImageUpload={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
              onImageRemove={() => setFormData(prev => ({ ...prev, logo_url: '' }))}
              label="Client Logo *"
              folder="client-logos"
            />

            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                name="website_url"
                type="url"
                value={formData.website_url}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                name="display_order"
                type="number"
                value={formData.display_order}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleChange}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_active">Active (visible on website)</Label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1"
              >
                {mutation.isPending ? 'Saving...' : (logo ? 'Update Logo' : 'Add Logo')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientLogoForm;
